import React, { useState, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import Lobby from './HostScreen/Lobby';
import Leaderboard from './Leaderboard';
import './HostScreen.css';

const screenVariants = {
  initial: { opacity: 0, x: '100%' },
  enter:   { opacity: 1, x: '0%' },
  exit:    { opacity: 0, x: '-50%' },
};

const HostScreen = ({
  socket, serverIP, sessionCreated, createSession, gameStarted,
  sessionId, players, rounds, setRounds, currentRound, sessionList,
  leaderboard, removePlayer, titleTheme, AudioPlayer, isEndScene,
  speakingTheme, guessingTheme, gameMode, setGameMode, currentLine,
  isEndGame, availableImprovScripts, selectedImprovScript, setSelectedImprovScript, setForceRemove, forceRemove, guessesMade, serverType,
  activeVote, voteBudgetRemaining, lineVoteFeed,
}) => {
  const [activeScreen, setActiveScreen] = useState('lobby');
  const isProductionServer = serverType === 'fayaz.fly.dev';

  useEffect(() => {
    if (!gameStarted) setActiveScreen('lobby');
    else if (isEndScene) setActiveScreen('guessing');
    else setActiveScreen('speaking');
  }, [gameStarted, isEndScene]);

  const renderSpeakerScreen = () => (
    <div className="App">
      <h2>Current Speaker</h2>
      <p>{currentLine?.text}</p>
      {gameMode === 'linejudge' && activeVote && (
        <div className="guess-block">
          <h3>Line Vote Open</h3>
          <p>
            Voting on <b>{activeVote.speakerName}</b>
          </p>
        </div>
      )}
      {gameMode === 'linejudge' && (
        <div className="guesses-container">
          <h3>Positive Vote Budgets</h3>
          {Object.entries(voteBudgetRemaining || {}).map(([name, remainingVotes]) => (
            <div key={name} className="guess-block">
              <span>{name}</span>
              <span>{remainingVotes} left</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderGuessingScreen = () => (
    <div className="App">
      <h2>{gameMode === 'linejudge' ? 'Line Vote Feed' : 'Guess the Adlibber!'}</h2>
      <div className="guesses-container">
        {gameMode === 'linejudge'
          ? lineVoteFeed.map((voteEvent, index) => (
              <div key={`${voteEvent.lineId}-${index}`} className="guess-block">
                {voteEvent.penalty ? (
                  <span className="incorrect">{voteEvent.speakerName} penalty: -{Math.abs(voteEvent.vote)}</span>
                ) : (
                  <>
                    <span>{voteEvent.speakerName}</span>
                    <div className="voters">
                      <span>{voteEvent.voterName}</span>
                      <span className={voteEvent.vote > 0 ? 'correct' : 'incorrect'}>
                        {voteEvent.vote > 0 ? '+1' : '-1'}
                      </span>
                    </div>
                  </>
                )}
              </div>
            ))
          : guessesMade.map((guess, index) => (
              <div key={index} className="guess-block">
                <span>{guess.guess}</span>
                <div className="voters">
                  <span className={guess.correct ? 'correct' : 'incorrect'}>
                    {guess.name}
                  </span>
                </div>
              </div>
            ))}
      </div>
    </div>
  );

  return (
    <div className="host-screen">
      <div className="title-bar">
        <div className="join-message">
          <h2>
            {isProductionServer
              ? 'Production is selected in the server picker. Players should choose Production on their devices; no room code is needed.'
              : <>
                  Join at <span className="red-text">Fayaz.One</span> in your browser!
                </>}
          </h2>
        </div>
        <div className="room-info">
          {!isProductionServer && <h2>Room Code: {serverIP}</h2>}
          <h4>Session: {sessionId}</h4>
        </div>
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={activeScreen}
          className="screen-container"
          variants={screenVariants}
          initial="initial"
          animate="enter"
          exit="exit"
          transition={{ type: "spring", stiffness: 120, damping: 14 }} // matches config.gentle
        >
          {activeScreen === 'lobby' && (
            <Lobby
              socket={socket}
              ipAddress={serverIP}
              sessionCreated={sessionCreated}
              createSession={createSession}
              gameStarted={gameStarted}
              sessionId={sessionId}
              players={players}
              rounds={rounds}
              setRounds={setRounds}
              currentRound={currentRound}
              sessionList={sessionList}
              leaderboard={leaderboard}
              removePlayer={removePlayer}
              titleTheme={titleTheme}
              AudioPlayer={AudioPlayer}
              isEndScene={isEndScene}
              speakingTheme={speakingTheme}
              guessingTheme={guessingTheme}
              gameMode={gameMode}
              setGameMode={setGameMode}
              currentLine={currentLine}
              isEndGame={isEndGame}
              availableImprovScripts={availableImprovScripts}
              selectedImprovScript={selectedImprovScript}
              setSelectedImprovScript={setSelectedImprovScript}
              setForceRemove={setForceRemove}
              forceRemove={forceRemove}
            />
          )}
          {activeScreen === 'speaking' && renderSpeakerScreen()}
          {activeScreen === 'guessing' && renderGuessingScreen()}
        </motion.div>
      </AnimatePresence>

      {activeScreen !== 'lobby' && (
        <div className="leaderboard-container">
          <Leaderboard leaderboard={leaderboard} players={players} />
        </div>
      )}
    </div>
  );
};

export default HostScreen;